import React, { useState } from 'react';
import JSZip from 'jszip';
import heic2any from 'heic2any';
import './App.css';

function App() {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(droppedFiles);
    
    for (const file of droppedFiles) {
      if (!file.type && !file.name.endsWith('.livp')) {
        alert('请选择有效的LIVP文件');
        return;
      }
      if (file.name.endsWith('.livp')) {
        await processLivpFile(file);
      }
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    
    for (const file of selectedFiles) {
      if (!file.type && !file.name.endsWith('.livp')) {
        alert('请选择有效的LIVP文件');
        return;
      }
      if (file.name.endsWith('.livp')) {
        await processLivpFile(file);
      }
    }
  };

  const processLivpFile = async (file) => {
    try {
      setProcessing(true);
      setProgress(0);

      // 首先检查文件类型
      if (!file.name.endsWith('.livp')) {
        throw new Error('请选择LIVP文件');
      }

      // 在 processLivpFile 函数开始时添加文件大小检查
      if (file.size < 100) { // 文件太小，可能已损坏
        throw new Error('LIVP文件可能已损坏或不完整');
      }

      // 读取文件为 Uint8Array
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const array = new Uint8Array(reader.result);
          resolve(array);
        };
        reader.onerror = () => reject(new Error('文件读取失败'));
        reader.readAsArrayBuffer(file);
      });

      setProgress(20);

      // 使用更严格的选项初始化 JSZip
      const zip = new JSZip();
      let zipContent;
      try {
        zipContent = await zip.loadAsync(arrayBuffer, {
          checkCRC32: true,
          decodeFileName: (bytes) => {
            return decodeURIComponent(escape(bytes));
          }
        });
      } catch (zipError) {
        console.error('ZIP解析错误:', zipError);
        throw new Error('LIVP文件格式无效或已损坏');
      }

      setProgress(40);

      // 查找文件
      const heicEntry = Object.values(zipContent.files).find(
        file => file.name.toLowerCase().endsWith('.heic')
      );
      const movEntry = Object.values(zipContent.files).find(
        file => file.name.toLowerCase().endsWith('.mov')
      );

      setProgress(60);

      // 提取文件内容
      let heicFile = null;
      let movFile = null;

      if (heicEntry) {
        try {
          heicFile = await heicEntry.async('blob');
        } catch (error) {
          console.error('HEIC提取错误:', error);
        }
      }

      setProgress(80);

      if (movEntry) {
        try {
          movFile = await movEntry.async('blob');
        } catch (error) {
          console.error('MOV提取错误:', error);
        }
      }

      setProgress(90);

      if (!heicFile && !movFile) {
        throw new Error('未在LIVP文件中找到有效的HEIC或MOV文件');
      }

      setProgress(100);
      return { heic: heicFile, mov: movFile };

    } catch (error) {
      console.error('处理LIVP文件时出错:', error);
      alert(`处理文件失败: ${error.message}`);
      throw error;
    } finally {
      setProcessing(false);
    }
  };

  const convertToJpg = async (file) => {
    try {
      setProcessing(true);
      const { heic } = await processLivpFile(file);
      
      if (!heic) {
        throw new Error('未找到HEIC文件');
      }

      // 转换HEIC为JPEG
      const jpegBlob = await heic2any({
        blob: heic,
        toType: 'image/jpeg',
        quality: 0.9
      });

      // 创建下载链接
      const url = URL.createObjectURL(jpegBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name.replace('.livp', '.jpg');
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('转换为JPG时出错:', error);
      alert('转换失败: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const extractHeic = async (file) => {
    try {
      setProcessing(true);
      const { heic } = await processLivpFile(file);
      
      if (!heic) {
        throw new Error('未找到HEIC文件');
      }

      // 创建下载链接
      const url = URL.createObjectURL(heic);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name.replace('.livp', '.heic');
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('提取HEIC时出错:', error);
      alert('提取失败: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const extractMov = async (file) => {
    try {
      setProcessing(true);
      const { mov } = await processLivpFile(file);
      
      if (!mov) {
        throw new Error('未找到MOV文件');
      }

      // 创建下载链接
      const url = URL.createObjectURL(mov);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name.replace('.livp', '.mov');
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('提取MOV时出错:', error);
      alert('提取失败: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>📷 LIVP转换器 ↔</h1>
        <div className="button-group">
          <button 
            className="convert-button" 
            onClick={() => files.forEach(convertToJpg)}
            disabled={!files.length || processing}
          >
            转换为JPG
          </button>
          <button 
            className="extract-button"
            onClick={() => files.forEach(extractHeic)}
            disabled={!files.length || processing}
          >
            提取HEIC
          </button>
          <button 
            className="extract-button"
            onClick={() => files.forEach(extractMov)}
            disabled={!files.length || processing}
          >
            提取MOV
          </button>
        </div>
        <div 
          className={`drop-zone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.querySelector('input[type="file"]').click()}
        >
          <div className="upload-icon">☁️</div>
          <p>拖放LIVP文件到这里</p>
          <p>或点击选择</p>
          <input
            type="file"
            accept=".livp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            multiple
          />
          {processing && (
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
        <p className="info-text">
          {files.length > 0 
            ? `已选择 ${files.length} 个文件` 
            : '✨ 在线批量转换LIVP文件，支持转换为JPG、提取HEIC和MOV格式'}
        </p>
      </header>
    </div>
  );
}

export default App; 